import { useToast, AlertStatus, UseToastOptions } from "@chakra-ui/react";
import { useCallback } from "react";
import axios, { AxiosError } from "axios";
import { AppApiError, isObject, isValidAppApiError } from "../types";
import { useTranslation } from "react-i18next";
import { i18n } from "i18next";
import { ApiError } from "../utils/apiClients/ApiError";
import { isWafBlockError } from "../utils/apiClients/WafBlockError";

function getApiErrorMessage(
  error: AppApiError,
  fallbackMessage: string,
  i18n: i18n,
): string {
  if (isObject(error.detail)) {
    const tKey = `ApiError.${error.detail.type}`;
    return i18n.exists(tKey)
      ? i18n.t(tKey)
      : error.detail.msg || fallbackMessage;
  }
  if (Array.isArray(error.detail)) {
    const firstDetail = error.detail[0];
    if (firstDetail && isObject(firstDetail))
      return firstDetail.msg || fallbackMessage;
    if (typeof firstDetail === "string") return firstDetail;
    return fallbackMessage;
  }
  return error.detail;
}

function getStatusTitle(status: number, fallback: string, i18n: i18n): string {
  const tKey = `ApiError.${status}`;
  const statusText = i18n.exists(tKey) ? i18n.t(tKey) : fallback;
  return `${status}: ${statusText}`;
}

type ToastInfo = { title: string; message: string | undefined };

function extractApiError(err: ApiError, i18n: i18n): ToastInfo {
  return {
    title: getStatusTitle(err.status, err.message, i18n),
    message: isValidAppApiError(err.body)
      ? getApiErrorMessage(err.body, err.message, i18n)
      : err.message,
  };
}

function extractAxiosErrorRaw(
  err: AxiosError,
  i18n: i18n,
  status: number | undefined,
): ToastInfo {
  const title =
    status !== undefined
      ? getStatusTitle(status, err.code ?? err.message, i18n)
      : `${err.code}`;
  const responseData = err.response?.data;
  const message = isValidAppApiError(responseData)
    ? getApiErrorMessage(responseData, err.message, i18n)
    : err.message;
  return { title, message };
}

// status/title/description は本フックが算出するため受け取らない。duration 等の表示制御のみ上書き可能にする。
type ToastOverrides = Omit<UseToastOptions, "status" | "title" | "description">;

type ErrorToast = (
  e: unknown,
  toastStatus?: AlertStatus,
  options?: ToastOverrides,
) => void;

export function useToastMessage(): ErrorToast {
  const { i18n } = useTranslation();
  const toast = useToast();
  return useCallback(
    (err: unknown, toastStatus = "error", options?: ToastOverrides) => {
      // WAFブロック時は既にalert表示とリロードを行っているため、
      // リロード中に同じ内容のトーストが出てちらつかないよう抑止する
      if (isWafBlockError(err)) return;

      let title: string;
      let message: string | undefined;

      if (err instanceof ApiError) {
        ({ title, message } = extractApiError(err, i18n));
      } else if (axios.isAxiosError(err)) {
        ({ title, message } = extractAxiosErrorRaw(
          err,
          i18n,
          err.response?.status,
        ));
      } else if (err instanceof Error) {
        title = err.name;
        message = err.message;
      } else if (typeof err === "string") {
        title = err;
      } else {
        title = "Internal Error";
      }

      toast({
        status: toastStatus,
        containerStyle: { whiteSpace: "pre-wrap" },
        isClosable: true,
        duration: 5000,
        ...options,
        title: title,
        description: message,
      });
    },
    [i18n, toast],
  );
}
